import type { Card, CardFace, SeriesInfo, TermInfo } from '@/types/card'
import type { CatalogOption } from '@/types/catalog'
import { dataBaseUrl } from './environment'
import { cultureCountFromBits } from '@/utils/culture'
import {
  resolveIllustrators,
  resolveVoiceActors,
} from '@/utils/cardCredits'
import {
  selectCurrentCatalog,
  sortCatalogs,
} from '@/utils/catalog'

interface Catalog {
  version?: number
  effective_at?: string
  series: SeriesInfo[]
  data?: {
    keywords: { path: string }
    glossary: { path: string }
  }
  keywords?: { path: string }
  glossary?: { path: string }
}

interface CatalogIndex {
  version?: number
  catalogs: {
    effective_at: string
    path: string
  }[]
}

interface CatalogCredits {
  illustrator?: string
  illustrators?: unknown
  voice_actor?: string
  voice_actors?: unknown
}

type CatalogFace = Omit<CardFace, keyof CatalogCredits | 'culture_count' | 'body_effects'> & CatalogCredits

type CatalogCard = Omit<Card, 'faces'> & CatalogCredits & {
  faces: CatalogFace[]
}

const catalogPromises = new Map<string, Promise<Catalog>>()
const cardsPromises = new Map<string, Promise<Card[]>>()
const termsPromises = new Map<string, Promise<TermInfo[]>>()
let catalogOptionsPromise: Promise<CatalogOption[]> | null = null

export function fetchCatalogOptions(): Promise<CatalogOption[]> {
  if (!catalogOptionsPromise) {
    catalogOptionsPromise = (async () => {
      const indexRes = await fetch(`${dataBaseUrl}/index.json`, { cache: 'no-store' })
      if (!indexRes.ok) throw new Error('Failed to fetch index.json')

      const index = await indexRes.json() as CatalogIndex
      if (!Array.isArray(index.catalogs)) throw new Error('Invalid index.json')
      const catalogs = index.catalogs.flatMap((candidate): CatalogOption[] => {
        if (
          typeof candidate?.effective_at !== 'string'
          || !candidate.effective_at
          || Number.isNaN(Date.parse(candidate.effective_at))
          || typeof candidate.path !== 'string'
          || !candidate.path
        ) return []
        return [{ effectiveAt: candidate.effective_at, path: candidate.path }]
      })
      if (catalogs.length === 0) throw new Error('No catalogs found in index.json')
      return sortCatalogs(catalogs)
    })()
  }
  return catalogOptionsPromise
}

function fetchCatalog(catalogPath: string): Promise<Catalog> {
  const cached = catalogPromises.get(catalogPath)
  if (cached) return cached

  const request = (async () => {
    const catalogRes = await fetch(`${dataBaseUrl}/${catalogPath}`)
    if (!catalogRes.ok) throw new Error(`Failed to fetch catalog: ${catalogPath}`)
    return catalogRes.json() as Promise<Catalog>
  })()
  catalogPromises.set(catalogPath, request)
  return request
}

function getDataPaths(catalog: Catalog): { keywords: { path: string }; glossary: { path: string } } {
  const keywords = catalog.data?.keywords ?? catalog.keywords
  const glossary = catalog.data?.glossary ?? catalog.glossary
  if (!keywords || !glossary) throw new Error('Invalid catalog data paths')
  return { keywords, glossary }
}

function splitBodyEffects(bodyText: string): string[] {
  return bodyText
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)
}

/**
 * カード取得後の事前処理。
 * - culture_count はバックエンド値ではなくフロント側で再計算する
 *   （無色を含む多色の扱いを 1 か所にまとめるため）
 * - body_effects は本文の改行を効果単位として扱う検索用の派生値
 * - CV・イラストレーターは面ごとに正規化し、面に項目がない旧データだけ
 *   カード直下の値を引き継ぐ。空文字・空配列は旧値で補わない
 */
function preprocessCard(card: CatalogCard): Card {
  return {
    ...card,
    faces: card.faces.map((face): CardFace => {
      const illustratorSource = face.illustrator !== undefined || face.illustrators !== undefined ? face : card
      const voiceActorSource = face.voice_actor !== undefined || face.voice_actors !== undefined ? face : card
      const illustrator = typeof illustratorSource.illustrator === 'string' ? illustratorSource.illustrator : ''
      const voiceActor = typeof voiceActorSource.voice_actor === 'string' ? voiceActorSource.voice_actor : ''

      return {
        ...face,
        culture_count: cultureCountFromBits(face.culture_bits),
        body_effects: splitBodyEffects(face.body_text),
        illustrator,
        illustrators: resolveIllustrators(illustratorSource.illustrators, illustrator),
        voice_actor: voiceActor,
        voice_actors: resolveVoiceActors(voiceActorSource.voice_actors, voiceActor),
      }
    }),
  }
}

export async function fetchCards(catalogPath: string): Promise<Card[]> {
  const cached = cardsPromises.get(catalogPath)
  if (cached) return cached

  const request = (async () => {
    const { series } = await fetchCatalog(catalogPath)

    const results = await Promise.all(
      series.map(async (s) => {
        const r = await fetch(`${dataBaseUrl}/${s.path}`)
        if (!r.ok) throw new Error(`Failed to fetch series: ${s.name}`)
        const data = await r.json()
        return (data['cards'] as CatalogCard[]).map(preprocessCard)
      })
    )

    return results.flat()
  })()
  cardsPromises.set(catalogPath, request)
  return request
}

/** キーワード能力 + 用語集 を合わせた、本文中で解説対象となる全用語を返す */
export async function fetchTerms(catalogPath: string): Promise<TermInfo[]> {
  const cached = termsPromises.get(catalogPath)
  if (cached) return cached

  const request = (async () => {
    const catalog = await fetchCatalog(catalogPath)
    const { keywords, glossary } = getDataPaths(catalog)
    const [kwRes, glRes] = await Promise.all([
      fetch(`${dataBaseUrl}/${keywords.path}`),
      fetch(`${dataBaseUrl}/${glossary.path}`),
    ])
    if (!kwRes.ok) throw new Error('Failed to fetch keywords')
    if (!glRes.ok) throw new Error('Failed to fetch glossary')
    const [kw, gl] = await Promise.all([
      kwRes.json() as Promise<TermInfo[]>,
      glRes.json() as Promise<TermInfo[]>,
    ])
    return [...kw, ...gl]
  })()
  termsPromises.set(catalogPath, request)
  return request
}

export async function fetchCardById(id: number): Promise<Card | undefined> {
  const catalogs = await fetchCatalogOptions()
  const cards = await fetchCards(selectCurrentCatalog(catalogs).path)
  return cards.find(c => c.card_id === id)
}

export function getImageUrl(path: string): string {
  return `${dataBaseUrl}/${path}`
}

const NO_SERIES_IMAGE = new Set(['BASIC', 'プライズ'])

export function getSeriesImageUrl(series: string): string | null {
  if (NO_SERIES_IMAGE.has(series)) return null
  return `${dataBaseUrl}/images/series/${series}.png`
}

// ---- 文明アイコン ----
// culture_bits → icon_modal_culture の番号
// 実際の画像を目視確認して作成したハードコードマップ
// bits: 無色=1, 光=2, 水=4, 闇=8, 火=16, 自然=32
// 光水闇火(bits=30)はアイコンなし → null
const CULTURE_ICON_MAP: ReadonlyMap<number, number> = new Map([
  [1,  0],  // 無色
  [2,  1],  // 光
  [4,  2],  // 水
  [8,  3],  // 闇
  [16, 4],  // 火
  [32, 5],  // 自然
  [6,  6],  // 光水
  [34, 7],  // 光自然
  [12, 8],  // 水闇
  [24, 9],  // 闇火
  [48, 10], // 火自然
  [10, 11], // 光闇
  [18, 12], // 光火
  [20, 13], // 水火
  [36, 14], // 水自然
  [40, 15], // 闇自然
  [14, 16], // 光水闇
  [22, 17], // 光水火
  [38, 18], // 光水自然
  [26, 19], // 光闇火
  [42, 20], // 光闇自然
  [50, 21], // 光火自然
  [28, 22], // 水闇火
  [44, 23], // 水闇自然
  [52, 24], // 水火自然
  [56, 25], // 闇火自然
  [62, 26], // 全5色
])

/**
 * 文明アイコン URL を返す。光水闇火(bits=30)などアイコンが存在しない場合は null。
 */
export function getCultureIconUrl(cultureBits: number): string | null {
  const index = CULTURE_ICON_MAP.get(cultureBits)
  if (index === undefined) return null
  const padded = index.toString().padStart(2, '0')
  return `${dataBaseUrl}/images/cultures/icon_modal_culture${padded}.png`
}

let culturesPreloaded = false
export function preloadCultureIcons() {
  if (culturesPreloaded) return
  culturesPreloaded = true
  for (const index of CULTURE_ICON_MAP.values()) {
    const padded = index.toString().padStart(2, '0')
    const img = new Image()
    img.src = `${dataBaseUrl}/images/cultures/icon_modal_culture${padded}.png`
  }
}
